'use client';

import Image, { type ImageWrappedProps } from '@eliadmin/core/src/components/image/image-wrapped';
import { useEffect, useRef, useState } from 'react';

const blank = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>')}`;
const pending = new Map<Element, () => void>();
let observer: IntersectionObserver | undefined;

function forget(node: Element) {
  pending.delete(node);
  observer?.unobserve(node);
  if (!pending.size) {
    observer?.disconnect();
    observer = undefined;
  }
}

export function observeViewportImage(node: Element, release: () => void) {
  if (typeof IntersectionObserver === 'undefined') {
    release();
    return;
  }
  try {
    observer ??= new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (
            !entry.isIntersecting ||
            !entry.intersectionRect.width ||
            !entry.intersectionRect.height
          )
            continue;
          const reveal = pending.get(entry.target);
          if (reveal) {
            forget(entry.target);
            reveal();
          }
        }
      },
      { rootMargin: '200px 0px' },
    );
    pending.set(node, release);
    observer.observe(node);
    return () => forget(node);
  } catch {
    forget(node);
    release();
  }
}

function DeferredPicture(props: ImageWrappedProps) {
  const picture = useRef<HTMLPictureElement>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const image = picture.current?.querySelector('img');
    if (!image) {
      setReady(true);
      return;
    }
    return observeViewportImage(image, () => setReady(true));
  }, []);
  return (
    <picture ref={picture} style={{ display: 'contents' }} data-viewport-image="true">
      {!ready && <source media="(scripting: enabled)" srcSet={blank} />}
      <Image {...props} alt={props.alt} />
    </picture>
  );
}

/** Secondary media only. SSR and no-JS retain the real image; critical images bypass this gate. */
export default function ViewportImage(props: ImageWrappedProps) {
  const src = typeof props.src === 'string' ? props.src : '';
  // The core AI badge wraps the image, so it cannot be a picture's direct child.
  if (
    props.priority ||
    props.preload ||
    props.loading === 'eager' ||
    props.fetchPriority === 'high' ||
    /[?&]ai=1(?:&|$)/.test(src)
  ) {
    return <Image {...props} alt={props.alt} />;
  }
  return <DeferredPicture {...props} />;
}
